import React, { useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Calendar } from "primereact/calendar";
import { Message } from "primereact/message";
import { Badge } from "primereact/badge";
import { Divider } from "primereact/divider";
import { toastify } from "../../../../utils/toast";
import { getItem } from "../../../../utils/localStorage";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { terminatePropertyManagerContractURL } from "../../../../utils/urls";

const TerminateContractDialog = ({ visible, onHide, contractData, onTerminationSuccess }) => {
    const [formData, setFormData] = useState({
        terminationReason: '',
        terminationDate: new Date()
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    // Handle form input changes
    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear error for this field
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.terminationReason || formData.terminationReason.trim().length === 0) {
            newErrors.terminationReason = 'Termination reason is required';
        } else if (formData.terminationReason.trim().length < 10) {
            newErrors.terminationReason = 'Termination reason must be at least 10 characters';
        }
        
        if (!formData.terminationDate) {
            newErrors.terminationDate = 'Termination date is required';
        } else {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const termDate = new Date(formData.terminationDate);
            termDate.setHours(0, 0, 0, 0);
            
            if (termDate > today) {
                newErrors.terminationDate = 'Termination date cannot be in the future';
            }
            
            // Check if termination date is before contract start date
            if (contractData?.startDate) {
                const startDate = new Date(contractData.startDate);
                startDate.setHours(0, 0, 0, 0);
                
                if (termDate < startDate) {
                    newErrors.terminationDate = 'Termination date cannot be before contract start date';
                }
            }
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission
    const handleSubmit = async () => {
        if (!validateForm()) {
            toastify("Please fix the form errors", "error");
            return;
        }

        if (!contractData?._id) {
            toastify("No contract selected for termination", "error");
            return;
        }

        try {
            setLoading(true);
            const facilityId = await getItem("selectedFacilityId");
            
            if (!facilityId) {
                toastify("No facility selected", "error");
                return;
            }

            console.log('Terminating contract:', contractData._id);

            const requestBody = {
                terminationReason: formData.terminationReason.trim(),
                terminationDate: formData.terminationDate.toISOString()
            };

            const response = await makeRequest2(
                `${terminatePropertyManagerContractURL}/${facilityId}/${contractData._id}`, 
                'PUT',
                requestBody
            );

            console.log('Termination response:', response);

            if (response?.success) {
                toastify("Contract terminated successfully", "success");
                
                // Reset form
                setFormData({
                    terminationReason: '',
                    terminationDate: new Date()
                });
                setErrors({});
                
                // Call success callback if provided
                if (onTerminationSuccess) {
                    onTerminationSuccess(response.data);
                }
                
                // Close dialog
                onHide();
            } else {
                const errorMessage = response?.error || "Failed to terminate contract";
                toastify(errorMessage, "error");
                console.error('Termination failed:', response);
            }
        } catch (error) {
            console.error('Error terminating contract:', error);
            toastify("Error terminating contract: " + error.message, "error");
        } finally {
            setLoading(false);
        }
    };

    // Handle dialog close
    const handleClose = () => {
        if (!loading) {
            // Reset form when closing
            setFormData({
                terminationReason: '',
                terminationDate: new Date()
            });
            setErrors({});
            onHide();
        }
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch (error) {
            return 'Invalid Date';
        }
    };

    // Dialog footer
    const dialogFooter = (
        <div className="d-flex justify-content-between">
            <Button 
                label="Cancel" 
                icon="pi pi-times" 
                onClick={handleClose} 
                className="p-button-secondary"
                disabled={loading}
            />
            <Button 
                label={loading ? "Terminating..." : "Terminate Contract"} 
                icon={loading ? "pi pi-spin pi-spinner" : "pi pi-trash"}
                onClick={handleSubmit} 
                className="p-button-danger"
                disabled={loading}
                loading={loading}
            />
        </div>
    );

    return (
        <Dialog
            visible={visible}
            onHide={handleClose}
            header="Terminate Contract"
            footer={dialogFooter}
            className="terminate-contract-dialog"
            style={{ width: '600px' }}
            modal
            closable={!loading}
        >
            <div className="terminate-contract-content">
                {/* Warning Message */}
                <Message 
                    severity="warn" 
                    text="Warning: Terminating this contract will remove property management from all associated apartments and cannot be undone." 
                    className="mb-4"
                />

                {/* Contract Information */}
                {contractData && (
                    <div className="mb-4">
                        <h6 className="mb-3 font-semibold text-gray-700">Contract Information</h6>
                        <div className="bg-gray-50 p-3 rounded border">
                            <div className="grid grid-cols-1 gap-2">
                                <div className="flex justify-between items-center py-2 border-b border-gray-200 last:border-b-0">
                                    <label className="font-semibold text-gray-700 text-sm">Contract Name:</label>
                                    <span className="text-gray-600 text-right">{contractData.contractName || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between items-center py-2 border-b border-gray-200 last:border-b-0">
                                    <label className="font-semibold text-gray-700 text-sm">Current Status:</label>
                                    <Badge 
                                        value={contractData.status || 'Unknown'} 
                                        severity={contractData.status === 'Active' ? 'success' : 'secondary'}
                                    />
                                </div>
                                <div className="flex justify-between items-center py-2 border-b border-gray-200 last:border-b-0">
                                    <label className="font-semibold text-gray-700 text-sm">Start Date:</label>
                                    <span className="text-gray-600 text-right">{formatDate(contractData.startDate)}</span>
                                </div>
                                <div className="flex justify-between items-center py-2 border-b border-gray-200 last:border-b-0">
                                    <label className="font-semibold text-gray-700 text-sm">End Date:</label>
                                    <span className="text-gray-600 text-right">{formatDate(contractData.endDate)}</span>
                                </div>
                                <div className="flex justify-between items-center py-2 border-b border-gray-200 last:border-b-0">
                                    <label className="font-semibold text-gray-700 text-sm">Customer:</label>
                                    <span className="text-gray-600 text-right">
                                        {contractData.customer 
                                            ? `${contractData.customer.firstName} ${contractData.customer.lastName}`
                                            : 'N/A'
                                        }
                                    </span>
                                </div>
                                <div className="flex justify-between items-center py-2 border-b border-gray-200 last:border-b-0">
                                    <label className="font-semibold text-gray-700 text-sm">Apartments Count:</label>
                                    <span className="text-gray-600 text-right">{contractData.totalUnits || contractData.units?.length || 0} apartment(s)</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <Divider />

                {/* Termination Form */}
                <div className="mb-4">
                    <h6 className="mb-3 font-semibold text-gray-700">Termination Details</h6>
                    
                    {/* Termination Date */}
                    <div className="mb-4">
                        <label htmlFor="terminationDate" className="block text-sm font-medium text-gray-700 mb-2">
                            Termination Date <span className="text-red-500">*</span>
                        </label>
                        <Calendar
                            id="terminationDate"
                            value={formData.terminationDate}
                            onChange={(e) => handleInputChange('terminationDate', e.value)}
                            placeholder="Select termination date"
                            dateFormat="dd/mm/yy"
                            maxDate={new Date()}
                            showIcon
                            className={`w-full ${errors.terminationDate ? 'p-invalid' : ''}`}
                            disabled={loading}
                        />
                        {errors.terminationDate && (
                            <small className="text-red-500 text-sm mt-1 block">
                                {errors.terminationDate}
                            </small>
                        )}
                    </div>

                    {/* Termination Reason */}
                    <div className="mb-4">
                        <label htmlFor="terminationReason" className="block text-sm font-medium text-gray-700 mb-2">
                            Termination Reason <span className="text-red-500">*</span>
                        </label>
                        <InputTextarea
                            id="terminationReason"
                            value={formData.terminationReason}
                            onChange={(e) => handleInputChange('terminationReason', e.target.value)}
                            placeholder="Please provide a detailed reason for terminating this contract (minimum 10 characters)"
                            rows={4}
                            className={`w-full ${errors.terminationReason ? 'p-invalid' : ''}`}
                            disabled={loading}
                            maxLength={500}
                        />
                        <div className="flex justify-between items-center mt-1">
                            {errors.terminationReason && (
                                <small className="text-red-500 text-sm">
                                    {errors.terminationReason}
                                </small>
                            )}
                            <small className="text-gray-500 text-sm ml-auto">
                                {formData.terminationReason.length}/500 characters
                            </small>
                        </div>
                    </div>

                    {/* Impact Information */}
                    <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                        <h6 className="text-red-700 mb-2 font-semibold">
                            <i className="pi pi-exclamation-triangle mr-2"></i>
                            Impact of Termination
                        </h6>
                        <ul className="text-gray-700 text-sm space-y-1 pl-4">
                            <li>• Contract status will be changed to "Terminated"</li>
                            <li>• Contract end date will be updated to termination date</li>
                            <li>• Next invoice date will be cleared (no future invoices)</li>
                            <li>• Auto-send feature will be disabled</li>
                            <li>• All associated apartments will be removed from property management</li>
                            <li>• Property manager assignment will be cleared from apartments</li>
                            <li>• Termination details will be recorded in edit history</li>
                            <li>• This action cannot be undone</li>
                        </ul>
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

export default TerminateContractDialog;